import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/ui/components/Input';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { abacus } from '@/assets/images/emojis';
import { useUser } from '@/features/user';
import { routes } from '@/routes/routes';
import {
  declaredIncomeSchema,
  useSubmitIncome,
} from '../../hooks/useSubmitIncome';
import { trackIncomeRejected } from '../../hooks/incomeValidation';
import styles from './IncomePage.module.scss';

const incomeSchema = z.object({ declaredIncome: declaredIncomeSchema });

type IncomeFormInput = z.input<typeof incomeSchema>;
type IncomeFormOutput = z.output<typeof incomeSchema>;

export function IncomePage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { submitIncome, isLoading } = useSubmitIncome();
  const rejectedCount = useRef(0);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<IncomeFormInput, unknown, IncomeFormOutput>({
    resolver: zodResolver(incomeSchema),
    defaultValues: {
      declaredIncome: user?.declaredIncome ? String(user.declaredIncome) : '',
    },
  });

  const onSubmit = async ({ declaredIncome }: IncomeFormOutput) => {
    if (await submitIncome(declaredIncome)) {
      navigate(routes.scoring.base);
    }
  };

  const onInvalid = () => {
    rejectedCount.current += 1;
    trackIncomeRejected(
      getValues('declaredIncome'),
      'page',
      rejectedCount.current,
    );
  };

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      noValidate
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <img src={abacus} alt="Abacus emoji" className={styles.emoji} />
          <h1 className={styles.title}>Care este venitul tău lunar net?</h1>
          <p className={styles.description}>
            Îl folosim pentru a-ți calcula o ofertă preliminară. Oferta finală
            se confirmă cu datele de la ANAF.
          </p>
        </div>

        <div className={styles.inputSection}>
          <Input
            label="Venit lunar net (lei)"
            autoFocus
            inputMode="numeric"
            autoComplete="off"
            placeholder="4500"
            errorText={errors.declaredIncome?.message}
            {...register('declaredIncome')}
          />
        </div>
      </div>

      <div className={styles.bottomSection}>
        <Button
          type="submit"
          trackingId="submit_declared_income"
          text="Continuă"
          rightIcon={<Icon name="arrow-right" />}
          loading={isLoading}
          className={styles.continueButton}
        />
      </div>
    </form>
  );
}

export default IncomePage;
